import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import React from "react";

import { JobSyncStatus } from "@react/domains/jobs";
import { Button } from "@react/ui-primitives/button";
import { Skeleton } from "@react/ui-primitives/skeleton";

import { MEMORY_SERVICE } from "@apis/memory/config";
import type { GithubRepo, Project } from "@apis/memory/models";

import { useQueries } from "../../../shared/hooks/useQueries.ts";
import { useMemoryActions } from "../hooks/useMemoryActions.ts";

interface CommentSyncStatusProps {
  readonly githubRepoId: GithubRepo["id"];
  readonly isSending: boolean;
  readonly onSync: () => void;
}

const CommentSyncStatus: FC<CommentSyncStatusProps> = ({
  githubRepoId,
  isSending,
  onSync,
}) => {
  const { getJobsQueryOptions } = useQueries();
  const latestJobs = useQuery(
    getJobsQueryOptions({
      limit: 1,
      order: "desc",
      resourceId: String(githubRepoId),
      type: MEMORY_SERVICE.JOB_TYPES.commentSync,
    }),
  );

  if (latestJobs.status !== "success") {
    return <Skeleton deferred variant="text-label" />;
  }

  return (
    <JobSyncStatus
      isSending={isSending}
      job={latestJobs.data.items[0]}
      onSync={onSync}
    />
  );
};

interface GithubRepoCardProps {
  readonly githubRepo: GithubRepo;
  readonly projectId: Project["id"];
}

export const GithubRepoCard: FC<GithubRepoCardProps> = ({
  githubRepo,
  projectId,
}) => {
  const { reindexGithubComments, removeGithubRepoFromProject } =
    useMemoryActions();

  const reindex = (): void => {
    reindexGithubComments.mutate({ githubRepoId: githubRepo.id, projectId });
  };

  const remove = (): void => {
    removeGithubRepoFromProject.mutate({
      githubRepoId: githubRepo.id,
      projectId,
    });
  };

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-3">
      <span className="text-sm">{githubRepo.fullName}</span>
      <div className="flex items-center gap-2">
        <CommentSyncStatus
          githubRepoId={githubRepo.id}
          isSending={reindexGithubComments.isPending}
          onSync={reindex}
        />
        <Button
          disabled={removeGithubRepoFromProject.isPending}
          onClick={remove}
          variant="destructive"
        >
          Remove
        </Button>
      </div>
    </div>
  );
};
